import { useEffect, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { getMerosFreigaben, speichereMerosFreigaben, type MerosMonteur } from "@/lib/meros";
import { toast } from "@/lib/toast";
import { MerosMitarbeiterWahl, type PersonenOption } from "@/pages/veranstaltungen/MerosMitarbeiterWahl";

function personenOptionen(rows: MerosMonteur[]): PersonenOption<string>[] {
  return rows.map((row) => ({
    id: row.login,
    label: row.label || row.login,
    hint: row.rolle === "app-admin" ? "App-Admin" : undefined,
  }));
}

export function MerosFreigabeKarte() {
  const [options, setOptions] = useState<PersonenOption<string>[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [saved, setSaved] = useState<string[]>([]);
  const [verzeichnis, setVerzeichnis] = useState(true);
  const [appAdminsWaehlbar, setAppAdminsWaehlbar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void getMerosFreigaben()
      .then((data) => {
        if (cancelled) return;
        setOptions(personenOptionen(data.monteure || []));
        setSelected(data.freigegeben || []);
        setSaved(data.freigegeben || []);
        setVerzeichnis(data.verzeichnis_verfuegbar !== false);
        setAppAdminsWaehlbar(data.app_admins_waehlbar === true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Die Freigabe konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty =
    selected.length !== saved.length || selected.some((login) => !saved.includes(login));

  async function save() {
    setSaving(true);
    try {
      const next = await speichereMerosFreigaben(selected);
      setOptions(personenOptionen(next.monteure || []));
      setSelected(next.freigegeben || []);
      setSaved(next.freigegeben || []);
      setVerzeichnis(next.verzeichnis_verfuegbar !== false);
      setAppAdminsWaehlbar(next.app_admins_waehlbar === true);
      setError(null);
      toast.success(
        next.freigegeben.length === 0
          ? "Kein Monteur hat Zugriff auf Meros."
          : next.freigegeben.length === 1
            ? "1 Monteur kann Meros öffnen."
            : `${next.freigegeben.length} Monteure können Meros öffnen.`,
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Die Freigabe konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Monteure"
      description={
        appAdminsWaehlbar
          ? "Diese Personen sehen Meros und können Termine zum Klappen eintragen. Auf der Dev-App sind zum Testen auch App-Admins auswählbar."
          : "Diese Personen sehen Meros und können Termine zum Klappen eintragen."
      }
    >
      {loading ? (
        <Spinner className="h-5 w-5" />
      ) : (
        <div className="space-y-3">
          {error ? <Alert variant="warning">{error}</Alert> : null}
          {!verzeichnis ? (
            <Alert variant="warning">
              Das Mitarbeiterverzeichnis ist gerade nicht erreichbar. Gespeicherte Freigaben bleiben sichtbar.
            </Alert>
          ) : null}
          <MerosMitarbeiterWahl
            options={options}
            selected={selected}
            disabled={saving || !verzeichnis}
            placeholder={appAdminsWaehlbar ? "Person suchen …" : "Monteur suchen …"}
            emptyText={appAdminsWaehlbar ? "Keine Personen gefunden." : "Keine Monteure gefunden."}
            onChange={setSelected}
          />
          <Button size="sm" onClick={() => void save()} loading={saving} disabled={!dirty || !verzeichnis}>
            Freigabe speichern
          </Button>
        </div>
      )}
    </Card>
  );
}
